import React from 'react';

import {authenticationSuccess, saveUser, validateAuthentication} from '@actions';

import {
    CHANNEL_NAME,
    LOCK_NAME,
    STALE_MS,
    canUseSsoCoordinator,
    clearLeaderInfo,
    deleteSavedOriginPathname,
    getLeaderInfo,
    getOrCreateTabId,
    isValidDoneUserPayload,
    isLeaderExpired,
    setLeaderInfo,
    redirectToLogin,
    restoreOriginPathnameIfNeeded,
} from '../utils/authenticateUtils';

const LOADER_MESSAGES = {
    DEFAULT: 'Checking authentication...',
    WAITING: 'Another tab is signing you in...',
    FINISHING: 'Finalizing sign-in...',
    REDIRECTING: 'Redirecting to sign-in...',
    TAKEOVER: 'Taking over sign-in...',
    REATTEMPTING: 'Retrying sign-in...',
    COMPLETED: 'Sign-in complete',
};

export default function useAuthenticateCoordinator({dispatch} = {}) {
    const isCoordinatorEnabled = React.useMemo(() => canUseSsoCoordinator(), []);
    const tabIdRef = React.useRef(getOrCreateTabId());
    const hasStartedRef = React.useRef(false);
    const isLockRequestInFlightRef = React.useRef(false);
    const hasCompletedRef = React.useRef(false);

    const [loaderMessage, setLoaderMessage] = React.useState(LOADER_MESSAGES.DEFAULT);
    const [showTakeoverButton, setShowTakeoverButton] = React.useState(false);
    const [remainingTtlMs, setRemainingTtlMs] = React.useState(null);

    const dispatchValidation = React.useCallback(() => {
        if ('function' !== typeof dispatch) {
            return Promise.resolve(false);
        }

        return Promise.resolve(dispatch(validateAuthentication()))
            .then((result) => Boolean(result))
            .catch(() => false);
    }, [dispatch]);

    const applyDonePayload = React.useCallback((user) => {
        if ('function' !== typeof dispatch || !isValidDoneUserPayload(user)) {
            return false;
        }

        dispatch(saveUser(user));
        dispatch(authenticationSuccess());

        // Clear saved pathname after successful authentication
        try {
            deleteSavedOriginPathname(tabIdRef.current);
        } catch {
            // Silently catch errors - don't break auth flow
        }

        return true;
    }, [dispatch]);

    const restoreOriginPathname = React.useCallback(() => {
        const tabId = tabIdRef.current;
        const currentPathname = window.location.pathname + window.location.search + window.location.hash;

        try {
            const savedPathname = restoreOriginPathnameIfNeeded(tabId, currentPathname);

            if (savedPathname) {
                window.location.replace(savedPathname);
            }
        } catch {
            // Silently catch - don't break auth flow
        }
    }, []);

    const getFreshLeader = React.useCallback(() => {
        const leader = getLeaderInfo();

        if (!leader) {
            return null;
        }

        if (isLeaderExpired(leader, STALE_MS)) {
            clearLeaderInfo();

            return null;
        }

        return leader;
    }, []);

    const setWaitingState = React.useCallback((leader) => {
        setLoaderMessage(LOADER_MESSAGES.WAITING);
        setShowTakeoverButton(true);

        if (leader && 'number' === typeof leader.timestamp) {
            setRemainingTtlMs(Math.max(0, STALE_MS - (Date.now() - leader.timestamp)));
        } else {
            setRemainingTtlMs(null);
        }
    }, []);

    const setRedirectingState = React.useCallback((message = LOADER_MESSAGES.REDIRECTING) => {
        setLoaderMessage(message);
        setShowTakeoverButton(false);
        setRemainingTtlMs(null);
    }, []);

    const markAuthenticationCompleted = React.useCallback(({user} = {}) => {
        if (hasCompletedRef.current) {
            return;
        }

        hasCompletedRef.current = true;
        clearLeaderInfo();

        // Restore pathname if needed before setting loader state
        restoreOriginPathname();

        setLoaderMessage(LOADER_MESSAGES.FINISHING);
        setShowTakeoverButton(false);
        setRemainingTtlMs(null);

        if (applyDonePayload(user)) {
            return;
        }

        dispatchValidation().then((isAuthenticated) => {
            if (isAuthenticated) {
                return;
            }

            hasCompletedRef.current = false;
            setWaitingState(getFreshLeader());
        });
    }, [applyDonePayload, dispatchValidation, getFreshLeader, setWaitingState, restoreOriginPathname]);

    const runLeaderElection = React.useCallback(async ({forceTakeover = false} = {}) => {
        if (!isCoordinatorEnabled) {
            redirectToLogin();

            return;
        }

        if (isLockRequestInFlightRef.current) {
            return;
        }

        isLockRequestInFlightRef.current = true;

        try {
            const leader = getFreshLeader();

            // A duplicated browser tab can clone tabId/sessionStorage; any fresh leader must block redirects.
            if (leader && !forceTakeover) {
                setWaitingState(leader);

                return;
            }

            await navigator.locks.request(LOCK_NAME, async () => {
                const leaderInsideLock = getFreshLeader();

                // Mandatory second check under lock to avoid queued-tab redirect races.
                if (leaderInsideLock && !forceTakeover) {
                    setWaitingState(leaderInsideLock);

                    return;
                }

                setLeaderInfo({
                    id: tabIdRef.current,
                    timestamp: Date.now(),
                });
                setRedirectingState(forceTakeover ? LOADER_MESSAGES.TAKEOVER : LOADER_MESSAGES.REDIRECTING);
                redirectToLogin();
            });
        } catch {
            setRedirectingState();
            redirectToLogin();
        } finally {
            isLockRequestInFlightRef.current = false;
        }
    }, [getFreshLeader, isCoordinatorEnabled, setRedirectingState, setWaitingState]);

    const start = React.useCallback(() => {
        if (hasStartedRef.current) {
            return;
        }

        hasStartedRef.current = true;
        runLeaderElection().catch(() => {
            hasStartedRef.current = false;
            setRedirectingState();
            redirectToLogin();
        });
    }, [runLeaderElection, setRedirectingState]);

    const takeover = React.useCallback(() => {
        if (!isCoordinatorEnabled) {
            redirectToLogin();

            return;
        }

        setLoaderMessage(LOADER_MESSAGES.TAKEOVER);
        runLeaderElection({forceTakeover: true});
    }, [isCoordinatorEnabled, runLeaderElection]);

    const reset = React.useCallback(({showCompleted = false} = {}) => {
        const nextLoaderMessage = showCompleted && hasCompletedRef.current
            ? LOADER_MESSAGES.COMPLETED
            : LOADER_MESSAGES.DEFAULT;

        hasStartedRef.current = false;
        isLockRequestInFlightRef.current = false;
        hasCompletedRef.current = false;
        setLoaderMessage(nextLoaderMessage);
        setShowTakeoverButton(false);
        setRemainingTtlMs(null);
    }, []);

    // On component mount, attempt to restore pathname if it was saved before SSO redirect
    // This handles the case where backend redirects the page and restoration wasn't triggered
    React.useEffect(() => {
        restoreOriginPathname();
    }, [restoreOriginPathname]);

    React.useEffect(() => {
        if (!isCoordinatorEnabled) {
            return undefined;
        }

        const channel = new window.BroadcastChannel(CHANNEL_NAME);

        channel.onmessage = (event) => {
            if ('done' !== event?.data?.type) {
                return;
            }

            if (hasCompletedRef.current) {
                return;
            }

            // Restore pathname if needed before setting loader state
            restoreOriginPathname();

            setLoaderMessage(LOADER_MESSAGES.FINISHING);
            setShowTakeoverButton(false);
            setRemainingTtlMs(null);
            markAuthenticationCompleted({user: event?.data?.user});
        };

        return () => {
            channel.close();
        };
    }, [isCoordinatorEnabled, markAuthenticationCompleted, restoreOriginPathname]);

    React.useEffect(() => {
        if (!isCoordinatorEnabled || !showTakeoverButton) {
            return undefined;
        }

        const intervalId = window.setInterval(() => {
            const leader = getFreshLeader();

            if (!leader) {
                if (null === remainingTtlMs) {
                    return;
                }

                hasStartedRef.current = false;
                setLoaderMessage(LOADER_MESSAGES.REATTEMPTING);
                setShowTakeoverButton(false);
                setRemainingTtlMs(null);
                runLeaderElection();

                return;
            }

            setRemainingTtlMs(Math.max(0, STALE_MS - (Date.now() - leader.timestamp)));
        }, 1000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [getFreshLeader, isCoordinatorEnabled, remainingTtlMs, runLeaderElection, showTakeoverButton]);

    return {
        loaderMessage,
        showTakeoverButton,
        remainingTtlMs,
        start,
        takeover,
        reset,
    };
}
